import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Warehouse, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function StockViewPage() {
  const navigate = useNavigate();
  const [balances, setBalances] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [godownFilter, setGodownFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [godownFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (godownFilter) params.godown_id = godownFilter;
      const [stockRes, godownsRes] = await Promise.all([
        inventoryAPI.getStock(params),
        inventoryAPI.listGodowns(),
      ]);
      setBalances(stockRes.data);
      setGodowns(godownsRes.data);
    } catch (err) {
      toast.error('Failed to load stock data');
    } finally { setLoading(false); }
  };

  // Group by godown
  const grouped = {};
  balances.forEach(b => {
    if (b.quantity <= 0) return;
    const key = b.godown_name || 'Unknown';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(b);
  });

  return (
    <div className="space-y-4" data-testid="stock-view-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Stock View</h1>
          <p className="text-xs text-muted-foreground">Current inventory across godowns</p>
        </div>
        <Select value={godownFilter} onValueChange={v => setGodownFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="godown-filter">
            <SelectValue placeholder="All Godowns" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Godowns</SelectItem>
            {godowns.map(g => <SelectItem key={g.id} value={g.id}>{g.code} - {g.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-center text-xs text-muted-foreground py-8">Loading...</p>
      ) : Object.keys(grouped).length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Warehouse className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No stock recorded yet</p>
            <p className="text-xs text-muted-foreground mt-1">Receive items to see stock here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([godownName, items]) => (
            <Card key={godownName}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Warehouse className="w-4 h-4 text-cyan-600" />
                  {godownName}
                  <Badge variant="secondary" className="text-[10px] ml-auto">{items.length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2.5 font-medium text-muted-foreground">Item</th>
                      <th className="text-right p-2.5 font-medium text-muted-foreground">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(b => (
                      <tr key={b.id || `${b.item_id}-${b.godown_id}`}
                        className="border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/inventory/items/${b.item_id}`)} data-testid={`stock-row-${b.item_id}`}>
                        <td className="p-2.5">
                          <span className="font-medium">{b.item_name}</span>
                        </td>
                        <td className="p-2.5 text-right font-mono font-bold">{b.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
